// src/pages/MyCateringBookings.jsx
/* eslint-disable react/prop-types */
/**
 * MyCateringBookings.jsx
 *
 * Production-ready page for managing/displaying catering bookings.
 *
 * Goals / features:
 * - Real-time listener via listenToBookings helper (falls back to Firestore snapshot if helper missing)
 * - Single scrollable responsive list (modern, mobile-friendly)
 * - Search + Status filter (All / Pending / Accepted / Confirmed / Completed / Cancelled)
 * - Admin tools:
 *    - Export visible CSV
 *    - Copy JSON (visible / all) to clipboard
 *    - Inline status edit for admins (with confirmation)
 * - Persistent user preferences (search / filter) via localStorage wrapper
 * - Accessibility improvements (aria, keyboard handlers)
 * - Defensive coding: helper fallback, safe clipboard/download, try/catch everywhere
 * - No eslint warnings for unused imports or missing hook deps
 *
 * Notes:
 * - Uses listenToBookings and updateBookingStatus from ../utils/firebase (your existing helpers).
 * - Keep the CSS file at ../styles/MyCateringBookings.css (I assume it exists and matches your palette).
 *
 * Author: assistant-updated
 * Date: 2025-11-09
 */

/* ======================================================================
   IMPORTS
   ====================================================================== */
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import PropTypes from "prop-types";
import "../styles/MyCateringBookings.css";
import { useAuth } from "../context/AuthContext";

import {
  listenToBookings as listenToBookingsHelper,
  updateBookingStatus as updateBookingStatusHelper,
} from "../utils/firebase";

import { getApp } from "firebase/app";
import {
  getFirestore,
  collection,
  query,
  where,
  orderBy,
  onSnapshot,
  doc as firestoreDoc,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";

import BookingPageShell from "../components/BookingPageShell"; // 🔽 ADDED IMPORT

/* ======================================================================
   CONFIG & CONSTANTS
   ====================================================================== */

/**
 * Admin emails (lowercase) - keep in sync with your auth admin list.
 * Add/remove as needed.
 */
const ADMIN_EMAILS = [
  "hariharanmadhav@gmail.com",
  "mugiwaraaluffy007@gmail.com",
  "lalithaganesan449@gmail.com",
];

/**
 * Firestore collection name for catering bookings
 */
const BOOKINGS_COLLECTION = "cateringBookings";

/**
 * Status ordering and valid statuses
 */
const STATUS_LIST = [
  "pending",
  "accepted",
  "confirmed",
  "completed",
  "cancelled",
];

/**
 * Debounce and other UI constants
 */
const SEARCH_DEBOUNCE_MS = 260;
const CSV_FILENAME_PREFIX = "catering-bookings";
const LOCALSTORAGE_KEY = "mycatering_bookings_state_v2";

/* ======================================================================
   SMALL UTILITIES
   ====================================================================== */

/**
 * normalize - safe string normalization for comparisons
 * @param {*} v
 * @returns {string}
 */
const normalize = (v) => (v || "").toString().toLowerCase().trim();

/**
 * formatDate - handles Firestore Timestamp, Date, number or string
 * returns a locale string or "Unknown" if invalid
 */
const formatDate = (val) => {
  if (!val && val !== 0) return "Unknown";
  if (typeof val?.toDate === "function") {
    try {
      return val.toDate().toLocaleString();
    } catch (e) {
      // fallback to string
      return String(val);
    }
  }
  if (val instanceof Date) return val.toLocaleString();
  const parsed = new Date(val);
  if (!isNaN(parsed.getTime())) return parsed.toLocaleString();
  return String(val);
};

/**
 * formatDateForCSV - returns ISO string when possible for CSV output
 */
const formatDateForCSV = (val) => {
  if (!val && val !== 0) return "";
  try {
    let date;
    if (typeof val?.toDate === "function") date = val.toDate();
    else if (val instanceof Date) date = val;
    else date = new Date(val);
    if (isNaN(date.getTime())) return String(val);
    return date.toISOString();
  } catch {
    return String(val);
  }
};

/**
 * toCSV - convert array of objects to CSV string given fields array
 * fields order is respected
 */
const toCSV = (arr = [], fields = []) => {
  const esc = (v = "") =>
    `"${String(v).replace(/"/g, '""').replace(/\r?\n/g, " ").replace(/\t/g, " ")}"`;
  const header = fields.map((f) => esc(f)).join(",");
  const rows = arr.map((item) =>
    fields.map((f) => {
      if (f === "createdAt" || f === "created_at") return esc(formatDateForCSV(item[f]));
      return esc(item[f] ?? "");
    }).join(",")
  );
  return [header, ...rows].join("\r\n");
};

/**
 * downloadBlob - safely trigger a download for small CSV/text
 */
const downloadBlob = (filename, content, mime = "text/csv;charset=utf-8;") => {
  try {
    const blob = new Blob([content], { type: mime });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename;
    // keep invisible and attach
    a.style.display = "none";
    document.body.appendChild(a);
    a.click();
    a.remove();
    setTimeout(() => URL.revokeObjectURL(url), 2000);
  } catch (err) {
    console.error("downloadBlob error", err);
    alert("Failed to download file. See console for details.");
  }
};

/**
 * safeClipboardWrite - copies text to clipboard with fallback
 * returns boolean success
 */
const safeClipboardWrite = async (text) => {
  try {
    if (navigator.clipboard && navigator.clipboard.writeText) {
      await navigator.clipboard.writeText(text);
      return true;
    }
    const ta = document.createElement("textarea");
    ta.value = text;
    ta.style.position = "fixed";
    ta.style.left = "-9999px";
    document.body.appendChild(ta);
    ta.focus();
    ta.select();
    const ok = document.execCommand("copy");
    document.body.removeChild(ta);
    return !!ok;
  } catch (e) {
    console.error("safeClipboardWrite failed", e);
    return false;
  }
};

/* ======================================================================
   SMALL PRESENTATIONAL COMPONENTS & ICONS
   ====================================================================== */

/**
 * IconRefresh - small inline svg
 */
const IconRefresh = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M21 12a9 9 0 1 0-3 6.36" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M21 3v6h-6" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * IconDownload - small inline svg
 */
const IconDownload = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M7 10l5 5 5-5" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M12 15V3" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/**
 * StatusPill - small presentational status chip; styling done via CSS classes
 */
const StatusPill = ({ status }) => {
  const s = normalize(status || "pending");
  const cls = `status-pill status-${s}`;
  return (
    <span className={cls} aria-label={`status ${s}`}>
      {s}
    </span>
  );
};
StatusPill.propTypes = { status: PropTypes.string };

/**
 * SmallSpinner - purely visual spinner used in UI
 */
const SmallSpinner = ({ size = 12 }) => (
  <span className="small-spinner" style={{ width: size, height: size }} aria-hidden />
);
SmallSpinner.propTypes = { size: PropTypes.number };

/**
 * Simple toast component for brief messages
 */
const Toast = ({ children }) => (
  <div className="mc-toast" role="status" aria-live="polite">
    {children}
  </div>
);
Toast.propTypes = { children: PropTypes.node };

/* ======================================================================
   BOOKING CARD - PRESENTATION (DETAILED)
   ====================================================================== */

/**
 * CateringBookingCard
 * - Avoid redundant role attribute (article already has implicit role)
 * - Keyboard accessible (enter/space)
 * - Provides admin inline controls when `isAdmin`
 */
const CateringBookingCard = React.memo(function CateringBookingCard({
  booking,
  isAdmin,
  onClick,
  onStatusChange,
  statusUpdatingMap,
  highlightQuery,
}) {
  const st = (booking.status || "pending").toString().toLowerCase();
  const updating = !!statusUpdatingMap?.[booking.id];

  // highlight support for search matches
  const highlight = (text) => {
    if (!highlightQuery) return text;
    try {
      const q = highlightQuery.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
      const re = new RegExp(q, "ig");
      return String(text).replace(re, (m) => `<mark class="hl">${m}</mark>`);
    } catch {
      return text;
    }
  };

  const stop = (e) => { if (e && e.stopPropagation) e.stopPropagation(); };

  return (
    <article
      className="booking-card"
      tabIndex={0}
      aria-labelledby={`bk-${booking.id}-title`}
      onClick={() => onClick && onClick(booking)}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") onClick && onClick(booking); }}
    >
      <div className="card-head">
        <div style={{ flex: 1 }}>
          <h3
            id={`bk-${booking.id}-title`}
            className="card-title"
            // using dangerouslySetInnerHTML solely for highlight markup inserted above
            dangerouslySetInnerHTML={{ __html: highlight(booking.eventName || booking.menu || "Catering Booking") }}
          />
          <div className="card-meta" style={{ marginTop: 8 }}>
            <span style={{ marginRight: 12 }}>
              <strong>{booking.guests ?? booking.persons ?? "—"}</strong> guests
            </span>
            <span>{booking.venue || booking.location || "Unknown venue"}</span>
          </div>
          <div style={{ marginTop: 8, color: "#64748b", fontSize: 13 }}>
            {booking.userEmail || booking.email || booking.phone || ""}
          </div>
        </div>

        <div style={{ minWidth: 150, textAlign: "right" }}>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, alignItems: "center" }}>
            <StatusPill status={st} />
          </div>

          {isAdmin && (
            <div style={{ marginTop: 12, display: "flex", gap: 8, justifyContent: "flex-end", alignItems: "center" }}>
              <div style={{ position: "relative" }}>
                <select
                  className="status-select"
                  value={st}
                  onClick={stop}
                  onChange={(e) => onStatusChange && onStatusChange(booking.id, e.target.value, booking)}
                  disabled={updating}
                  aria-label={`Change status for booking ${booking.id}`}
                >
                  {STATUS_LIST.map((s) => (
                    <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>
                  ))}
                </select>
                {updating && <div className="card-spinner" aria-hidden />}
              </div>

              <button
                className="inline-btn cancel-quick"
                onClick={(e) => {
                  stop(e);
                  const next = "cancelled";
                  if (st === next) return;
                  if (window.confirm(`Mark booking "${booking.eventName || booking.id}" as cancelled?`)) {
                    onStatusChange && onStatusChange(booking.id, next, booking);
                  }
                }}
                title="Quick cancel"
                aria-label={`Quick cancel booking ${booking.id}`}
                disabled={updating}
              >
                ❌
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="card-body" aria-hidden>
        <div className="card-row">
          <div><b>Date:</b> {booking.date || "TBD"}</div>
          <div><b>Time:</b> {booking.time || "TBD"}</div>
        </div>

        <div className="card-row" style={{ marginTop: 4 }}>
          <div style={{ flex: 1 }}>
            <b>Menu:</b> <span dangerouslySetInnerHTML={{ __html: highlight(booking.menu || booking.eventName || "—") }} />
          </div>
        </div>

        <div className="card-row" style={{ marginTop: 4 }}>
          <div style={{ flex: 1 }}>
            <b>Notes:</b> <span style={{ whiteSpace: "pre-wrap" }} dangerouslySetInnerHTML={{ __html: highlight(booking.notes || "—") }} />
          </div>
        </div>
      </div>

      <div className="card-foot">
        <div>Booked on: {formatDate(booking.createdAt)}</div>
        <div style={{ color: "#94a3b8", fontSize: 13 }}>{booking.id}</div>
      </div>
    </article>
  );
});
CateringBookingCard.displayName = "CateringBookingCard";
CateringBookingCard.propTypes = {
  booking: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  onClick: PropTypes.func,
  onStatusChange: PropTypes.func,
  statusUpdatingMap: PropTypes.object,
  highlightQuery: PropTypes.string,
};

/* ======================================================================
   HOOKS
   ====================================================================== */

/**
 * useLocalState - small wrapper that loads initial value from localStorage (if present)
 * and persists on change. Returns [state, setState].
 */
function useLocalState(key, initial) {
  const [state, setState] = useState(() => {
    try {
      const raw = localStorage.getItem(key);
      if (raw) {
        return JSON.parse(raw);
      }
    } catch (e) {
      // ignore parsing errors and return initial
      // console.warn("useLocalState parse error", e);
    }
    return typeof initial === "function" ? initial() : initial;
  });

  useEffect(() => {
    try {
      localStorage.setItem(key, JSON.stringify(state));
    } catch (e) {
      // ignore (e.g., storage quota)
    }
  }, [key, state]);

  return [state, setState];
}

/**
 * useDebouncedValue - returns a debounced version of a value
 */
function useDebouncedValue(value, ms = 260) {
  const [v, setV] = useState(value);
  useEffect(() => {
    const t = setTimeout(() => setV(value), ms);
    return () => clearTimeout(t);
  }, [value, ms]);
  return v;
}

/* ======================================================================
   MAIN PAGE
   ====================================================================== */

/**
 * MyCateringBookings - main exported component
 *
 * Admin controls:
 * - exportCSV (visible bookings)
 * - copyJSON (visible / all)
 * - inline status edit (with confirmation and optional note)
 *
 * NOTE: persisted search/filter are loaded on creation to avoid effect dependency issues.
 */
const MyCateringBookings = () => {
  const { currentUser, loading: authLoading } = useAuth();

  // Persisted settings are read synchronously when component mounts.
  // The useLocalState hook handles initial load from localStorage.
  const [persistedState, setPersistedState] = useLocalState(LOCALSTORAGE_KEY, {
    filterStatus: "all",
    search: "",
  });

  // Use persisted state to set initial values; this avoids having a useEffect that references
  // persistedState (which triggers lint warnings about missing deps).
  const [search, setSearch] = useState(persistedState.search || "");
  const debouncedSearch = useDebouncedValue(search, SEARCH_DEBOUNCE_MS);

  const [filterStatus, setFilterStatus] = useState(persistedState.filterStatus || "all");

  // bookings and related UI state
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true); // local loading (authLoading separate)
  const [error, setError] = useState("");
  const [refreshKey, setRefreshKey] = useState(0);
  const [detail, setDetail] = useState(null);
  const [statusUpdatingMap, setStatusUpdatingMap] = useState({});
  const [message, setMessage] = useState("");

  // admin detection (memoized)
  const isAdminMemo = useMemo(() => {
    if (!currentUser) return false;
    return ADMIN_EMAILS.includes((currentUser.email || "").toLowerCase());
  }, [currentUser]);

  const [isAdmin, setIsAdmin] = useState(isAdminMemo);
  useEffect(() => setIsAdmin(isAdminMemo), [isAdminMemo]);

  // refs
  const isMountedRef = useRef(true);
  const modalRef = useRef(null);

  useEffect(() => {
    isMountedRef.current = true;
    return () => { isMountedRef.current = false; };
  }, []);

  // Keep the persisted state in sync when filter/search change
  useEffect(() => {
    // update persisted state
    setPersistedState((prev) => ({ ...prev, filterStatus, search }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterStatus, search]); // setPersistedState stable from hook

  // keyboard shortcut for refresh (press 'r' when not in input)
  useEffect(() => {
    const onKey = (e) => {
      const tag = document.activeElement?.tagName?.toLowerCase();
      if (e.key === "r" && !e.ctrlKey && !e.metaKey && !["input", "textarea", "select"].includes(tag)) {
        setRefreshKey((k) => k + 1);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  /* ============================
     Real-time listener: listenToBookings helper preferred
     ============================ */
  useEffect(() => {
    if (authLoading) {
      setLoading(true);
      return;
    }

    // if not logged and not admin: show empty
    if (!currentUser && !isAdmin) {
      setBookings([]);
      setLoading(false);
      setError("");
      return;
    }

    setLoading(true);
    setError("");

    // if the helper is available, use it
    if (typeof listenToBookingsHelper === "function") {
      try {
        const uidOrNull = isAdmin ? null : currentUser?.uid ?? null;
        const unsub = listenToBookingsHelper("catering", uidOrNull, (docs) => {
          if (!isMountedRef.current) return;
          setBookings(Array.isArray(docs) ? docs : []);
          setLoading(false);
          setError("");
        }, isAdmin);

        return () => {
          try { if (typeof unsub === "function") unsub(); } catch (e) { /* ignore */ }
        };
      } catch (err) {
        console.error("listenToBookingsHelper error:", err);
        // fallthrough to firestore fallback
      }
    }

    // Firestore fallback for real-time snapshot
    try {
      const app = getApp();
      const db = getFirestore(app);
      const col = collection(db, BOOKINGS_COLLECTION);

      let q;
      if (isAdmin) {
        q = query(col, orderBy("createdAt", "desc"));
      } else {
        q = query(col, where("uid", "==", currentUser?.uid ?? "NO_UID"), orderBy("createdAt", "desc"));
      }

      const unsub = onSnapshot(q, (snapshot) => {
        if (!isMountedRef.current) return;
        try {
          const docs = snapshot.docs.map((d) => ({ id: d.id, ...d.data() }));
          setBookings(docs);
          setLoading(false);
          setError("");
        } catch (e) {
          console.error("onSnapshot processing error", e);
          setBookings([]);
          setLoading(false);
          setError("Failed to parse bookings; see console.");
        }
      }, (err) => {
        console.error("onSnapshot error:", err);
        setBookings([]);
        setLoading(false);
        setError("Realtime connection error; see console for details.");
      });

      return () => {
        try { unsub(); } catch (e) { /* ignore */ }
      };
    } catch (err) {
      console.error("realtime listener fallback failed:", err);
      setBookings([]);
      setLoading(false);
      setError("Failed to initialize realtime listener.");
    }
  }, [authLoading, currentUser, isAdmin, refreshKey]);

  /* ============================
     Derived lists: filtering, sorting
     ============================ */
  const visible = useMemo(() => {
    const term = normalize(debouncedSearch);
    const arr = (bookings || []).filter((b) => {
      if (filterStatus !== "all") {
        const raw = b.status ?? "pending";
        if (normalize(raw) !== normalize(filterStatus)) return false;
      }
      if (!term) return true;
      const hay = `${b.menu || ""} ${b.venue || b.location || ""} ${b.notes || ""} ${b.eventName || ""} ${b.userEmail || b.email || ""}`.toLowerCase();
      return hay.includes(term);
    });

    // sort by status (using STATUS_LIST order) then createdAt desc
    arr.sort((a, b) => {
      const idx = (v) => {
        const s = (v || "pending").toString();
        const i = STATUS_LIST.findIndex((ss) => normalize(ss) === normalize(s));
        return i === -1 ? STATUS_LIST.length : i;
      };
      const ai = idx(a.status);
      const bi = idx(b.status);
      if (ai !== bi) return ai - bi;
      const ta = a.createdAt?.toDate ? a.createdAt.toDate().getTime() : new Date(a.createdAt || 0).getTime();
      const tb = b.createdAt?.toDate ? b.createdAt.toDate().getTime() : new Date(b.createdAt || 0).getTime();
      return tb - ta;
    });

    return arr;
  }, [bookings, debouncedSearch, filterStatus]);

  const counts = useMemo(() => {
    const m = { all: (bookings || []).length };
    for (const s of STATUS_LIST) m[s] = 0;
    for (const b of bookings || []) {
      const raw = b.status ?? "pending";
      const matched = STATUS_LIST.find((ss) => normalize(ss) === normalize(raw));
      if (matched) m[matched] = (m[matched] || 0) + 1;
    }
    return m;
  }, [bookings]);

  /* ============================
     State helpers & actions
     ============================ */

  const setUpdatingFor = useCallback((id, val) => {
    setStatusUpdatingMap((prev) => ({ ...prev, [id]: !!val }));
  }, []);

  const setTempMessage = useCallback((text, duration = 1800) => {
    setMessage(text);
    if (duration > 0) {
      setTimeout(() => setMessage(""), duration);
    }
  }, []);

  const handleRefresh = useCallback(() => {
    setRefreshKey((k) => k + 1);
    setTempMessage("Refreshed bookings", 1200);
  }, [setTempMessage]);

  const exportCSV = useCallback(() => {
    if (!visible || visible.length === 0) {
      alert("No visible bookings to export.");
      return;
    }
    const flat = visible.map((b) => ({
      id: b.id,
      eventName: b.eventName || b.menu || "",
      date: b.date || "",
      time: b.time || "",
      guests: b.guests ?? b.persons ?? "",
      venue: b.venue || b.location || "",
      status: b.status || "",
      email: b.userEmail || b.email || "",
      createdAt: formatDate(b.createdAt),
      notes: b.notes || "",
    }));
    const fields = ["id", "eventName", "date", "time", "guests", "venue", "status", "email", "createdAt", "notes"];
    const csv = toCSV(flat, fields);
    const filename = `${CSV_FILENAME_PREFIX}-${new Date().toISOString().slice(0, 10)}.csv`;
    downloadBlob(filename, csv);
  }, [visible]);

  const copyJSON = useCallback(async (scope = "visible") => {
    if (!isAdmin) {
      alert("Copy JSON is restricted to administrators only.");
      return;
    }
    try {
      let payload = [];
      if (scope === "page") payload = visible; // no pages here, treat as visible
      else if (scope === "visible") payload = visible;
      else payload = bookings;
      const ok = await safeClipboardWrite(JSON.stringify(payload, null, 2));
      if (ok) setTempMessage("JSON copied to clipboard", 1400);
      else alert("Copy failed.");
    } catch (e) {
      console.error("copyJSON failed:", e);
      alert("Copy failed. See console for details.");
    }
  }, [isAdmin, bookings, visible, setTempMessage]);

  /**
   * performUpdateBookingStatus - update booking status using helper or Firestore fallback
   * - optimistic UI update
   */
  const performUpdateBookingStatus = useCallback(async (bookingId, newStatus, options = {}) => {
    if (!isAdmin) {
      alert("Only admin users can change booking status.");
      return;
    }
    if (!bookingId || !newStatus) return;

    const canonical = STATUS_LIST.find((s) => normalize(s) === normalize(newStatus)) || newStatus;

    const prevBookings = bookings.slice();
    const prevBooking = bookings.find((b) => b.id === bookingId);
    const prevStatusRaw = prevBooking?.status ?? "pending";
    if (normalize(prevStatusRaw) === normalize(canonical)) return;

    if (normalize(canonical) === normalize("cancelled") && !options?.skipConfirm) {
      if (!window.confirm("Are you sure you want to cancel this booking?")) return;
    }

    let note = options?.note ?? "";
    try {
      const p = window.prompt("Optional note for status change (leave empty to skip):", "");
      if (typeof p === "string") note = p.trim();
    } catch (e) {
      note = options?.note ?? "";
    }

    setUpdatingFor(bookingId, true);
    setBookings((prev) => prev.map((b) => (b.id === bookingId ? { ...b, status: canonical, adminNote: note || b.adminNote } : b)));

    // Prefer helper if available
    if (typeof updateBookingStatusHelper === "function") {
      try {
        await updateBookingStatusHelper("catering", bookingId, canonical, { note });
        setUpdatingFor(bookingId, false);
        setTempMessage(`Status updated to ${canonical}`, 1400);
        return;
      } catch (err) {
        console.error("updateBookingStatusHelper failed:", err);
        setBookings(prevBookings);
        setUpdatingFor(bookingId, false);
        alert("Failed to update booking status via helper. See console.");
        return;
      }
    }

    // Firestore fallback
    try {
      const app = getApp();
      const db = getFirestore(app);
      await updateDoc(firestoreDoc(db, BOOKINGS_COLLECTION, bookingId), {
        status: canonical,
        ...(note ? { adminNote: note } : {}),
        updatedAt: serverTimestamp ? serverTimestamp() : new Date(),
      });
      setUpdatingFor(bookingId, false);
      setTempMessage(`Status updated to ${canonical}`, 1400);
      return;
    } catch (err) {
      console.error("Firestore update failed:", err);
      setBookings(prevBookings);
      setUpdatingFor(bookingId, false);
      alert("Failed to update booking status. See console for details.");
    }
  }, [bookings, isAdmin, setUpdatingFor, setTempMessage]);

  const onStatusChange = useCallback((id, nv) => {
    if (!nv) return;
    const canonical = STATUS_LIST.find((s) => normalize(s) === normalize(nv)) || nv;
    performUpdateBookingStatus(id, canonical, {});
  }, [performUpdateBookingStatus]);

  /* ============================
     Modal focus and escape handling
     ============================ */
  useEffect(() => {
    if (!detail) return;
    const el = modalRef.current;
    if (!el) return;
    const first = el.querySelector("button, a, input, select, textarea, [tabindex]");
    if (first) first.focus();
    const onKey = (e) => { if (e.key === "Escape") setDetail(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [detail]);

  /* ============================
     RENDER PATHS
     ============================ */

  // Loading UI (when auth or local data still loading)
  if (authLoading || loading) {
    return (
      <div className="my-catering-page">
        <div className="mc-header">
          <div className="mc-left">
            <div className="mc-title"><h1>My Catering Bookings</h1></div>
            {isAdmin && <span className="admin-badge">Admin</span>}
            <div className="toolbar-stats">Loading…</div>
          </div>
          <div className="mc-controls" aria-hidden>
            <div className="ch-control">
              <label>Search</label>
              <input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button className="btn small ghost" onClick={handleRefresh}><IconRefresh /> Refresh</button>
            </div>
          </div>
        </div>

        <div style={{ display: "grid", gap: 12 }}>
          <div className="skeleton skel-card" style={{ height: 160 }} />
          <div className="skeleton skel-card" style={{ height: 160 }} />
          <div className="skeleton skel-card" style={{ height: 160 }} />
          <div className="skeleton skel-card" style={{ height: 160 }} />
          <div className="skeleton skel-card" style={{ height: 160 }} />
        </div>
      </div>
    );
  }

  // Error UI
  if (error) {
    return (
      <div className="my-catering-page">
        <div className="mc-header">
          <div className="mc-left">
            <div className="mc-title"><h1>My Catering Bookings</h1></div>
            {isAdmin && <span className="admin-badge">Admin</span>}
          </div>

          <div className="mc-controls">
            <div className="ch-control">
              <label>Filter</label>
              <select value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)}>
                <option value="all">All ({bookings.length})</option>
                {STATUS_LIST.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)} ({counts[s] ?? 0})</option>)}
              </select>
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
              <button className="btn" onClick={handleRefresh}><IconRefresh /> Retry</button>
            </div>
          </div>
        </div>

        <div className="error">
          <h2>Could not load bookings</h2>
          <p>{String(error)}</p>
        </div>
      </div>
    );
  }

  // Main UI
  return (
    <BookingPageShell // ✅ WRAPPED
      title={isAdmin ? "All Catering Bookings" : "My Catering Bookings"}
      isAdmin={isAdmin}
    >
      <main className="my-catering-page" role="main">
        <header className="mc-header" aria-label="Bookings header">
          <div className="mc-left">
            <div className="mc-title"><h1>{isAdmin ? "All Catering Bookings" : "My Catering Bookings"}</h1></div>
            {isAdmin && <span className="admin-badge">Admin</span>}

            <div className="toolbar-stats" role="status" aria-live="polite" style={{ marginLeft: 8 }}>
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div>Visible: <strong>{visible.length}</strong></div>
                <div>All: <strong>{counts.all ?? 0}</strong></div>
              </div>

              <div className="mc-stats">
                <div className="stat-item">Pending: <strong>{counts["pending"] ?? 0}</strong></div>
                <div className="stat-item">Accepted: <strong>{counts["accepted"] ?? 0}</strong></div>
                <div className="stat-item">Confirmed: <strong>{counts["confirmed"] ?? 0}</strong></div>
                <div className="stat-item">Completed: <strong>{counts["completed"] ?? 0}</strong></div>
                <div className="stat-item">Cancelled: <strong>{counts["cancelled"] ?? 0}</strong></div>
              </div>
            </div>
          </div>

          <div className="mc-controls" role="region" aria-label="Controls">
            <div className="ch-control">
              <label htmlFor="mc-search">Search</label>
              <input id="mc-search" placeholder="Search menu, venue, notes, email..." value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search bookings" />
            </div>

            <div className="ch-control">
              <label htmlFor="mc-status">Status</label>
              <select id="mc-status" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} aria-label="Filter by status">
                <option value="all">All ({bookings.length})</option>
                {STATUS_LIST.map((s) => (
                  <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)} ({counts[s] ?? 0})</option>
                ))}
              </select>
            </div>

            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button className="btn small" onClick={handleRefresh} title="Refresh (press R)"><IconRefresh /> Refresh</button>

              {/* Export CSV visible for admins only */}
              {isAdmin && (
                <button className="btn small ghost" onClick={exportCSV} title="Export visible to CSV"><IconDownload /> Export CSV</button>
              )}

              {/* Copy JSON only for admins */}
              {isAdmin && (
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <button className="btn small ghost" onClick={() => copyJSON("visible")} title="Copy visible JSON">Copy JSON (visible)</button>
                  <button className="btn small ghost" onClick={() => copyJSON("all")} title="Copy all JSON">Copy JSON (all)</button>
                </div>
              )}
            </div>
          </div>
        </header>

        <section aria-label="Bookings list" style={{ marginTop: 12 }}>
          {visible.length === 0 ? (
            <div className="empty">
              <h2>No bookings</h2>
              <p>{isAdmin ? "No catering bookings found in the system." : "You have not made any catering bookings yet."}</p>
              <div style={{ marginTop: 12 }}>
                <button className="btn" onClick={handleRefresh}>Retry</button>
              </div>
            </div>
          ) : (
            <>
              <div className="mc-grid" aria-label="Bookings grid">
                {visible.map((b, idx) => (
                  <CateringBookingCard
                    key={b.id}
                    booking={b}
                    isAdmin={isAdmin}
                    onClick={(bk) => setDetail(bk)}
                    onStatusChange={onStatusChange}
                    statusUpdatingMap={statusUpdatingMap}
                    highlightQuery={debouncedSearch}
                  />
                ))}
              </div>

              <div style={{ marginTop: 16, display: "flex", justifyContent: "center", gap: 12 }}>
                <div className="small-muted">Showing <strong>{visible.length}</strong> booking{visible.length === 1 ? "" : "s"}</div>
              </div>
            </>
          )}
        </section>

        {/* Details modal */}
        {detail && (
          <div className="modal-backdrop" role="dialog" aria-modal="true" onClick={() => setDetail(null)}>
            <div className="modal" ref={modalRef} onClick={(e) => e.stopPropagation()}>
              <div style={{ display: "flex", justifyContent: "space-between", gap: 12 }}>
                <div style={{ flex: 1 }}>
                  <h2 style={{ margin: 0 }}>{detail.eventName || detail.menu || "Catering booking"}</h2>
                  <div style={{ color: "#64748b", marginTop: 6 }}>{detail.venue || detail.location || "No venue"}</div>
                  <div style={{ marginTop: 8 }} className="small-muted">Booking ID: {detail.id}</div>
                </div>

                <div style={{ textAlign: "right" }}>
                  <span className={`status-pill status-${(detail.status || "pending").toLowerCase()}`}>{(detail.status || "pending")}</span>

                  <div style={{ marginTop: 8, color: "#475569" }}>
                    <div>Booked: {formatDate(detail.createdAt)}</div>
                    <div style={{ marginTop: 6 }}>{detail.userEmail || detail.email || ""}</div>
                  </div>
                </div>
              </div>

              <div style={{ marginTop: 12, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
                <div>
                  <div style={{ marginBottom: 8 }}><b>Date & time</b></div>
                  <div>{detail.date || "TBD"} {detail.time ? `at ${detail.time}` : ""}</div>

                  <div style={{ marginTop: 12 }}><b>Guests</b></div>
                  <div>{detail.guests ?? detail.persons ?? "—"}</div>

                  <div style={{ marginTop: 12 }}><b>Contact</b></div>
                  <div>{detail.userEmail || detail.email || detail.phone || "—"}</div>
                </div>

                <div>
                  <div style={{ marginBottom: 8 }}><b>Menu summary</b></div>
                  <div>{detail.menu || "—"}</div>

                  <div style={{ marginTop: 12 }}><b>Notes</b></div>
                  <div style={{ whiteSpace: "pre-wrap" }}>{detail.notes || "—"}</div>
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginTop: 14 }}>
                {isAdmin && (
                  <div style={{ display: "flex", gap: 8, alignItems: "center", marginRight: "auto" }}>
                    <label style={{ fontWeight: 600, fontSize: 14, color: "#475569" }}>Status:</label>
                    <select className="status-select" value={(detail.status || "pending").toLowerCase()} onChange={(e) => {
                      const nv = e.target.value;
                      performUpdateBookingStatus(detail.id, nv, {});
                      setDetail((prev) => ({ ...prev, status: nv }));
                    }}>
                      {STATUS_LIST.map((s) => <option key={s} value={s}>{s[0].toUpperCase() + s.slice(1)}</option>)}
                    </select>
                  </div>
                )}

                {isAdmin && (
                  <button className="btn ghost small" onClick={() => { copyJSON("visible"); setDetail(null); }}>
                    Copy JSON
                  </button>
                )}

                <button className="btn" onClick={() => setDetail(null)}>Close</button>
              </div>
            </div>
          </div>
        )}

        {message && <Toast>{message}</Toast>}
      </main>
    </BookingPageShell> // ✅ CLOSED
  );
};

export default MyCateringBookings;

/* ========================================================================
   End of MyCateringBookings.jsx
   ========================================================================
*/